import type {
  PublicVesselGroup,
  PublicVesselSummary,
} from "../shared/contracts";
import { vesselDisplayName } from "./format";
import { projectVesselFix, projectedCorridorProgress } from "./scene/vessel-motion";
import { displayRouteBounds } from "./scene/vessel-formation";

export interface SingleVesselMovement {
  kind: "single";
  id: string;
  vessels: [PublicVesselSummary];
}

export interface LikelyTowMovement {
  kind: "likely_tow";
  id: string;
  group: PublicVesselGroup;
  vessels: PublicVesselSummary[];
  tugs: [PublicVesselSummary, ...PublicVesselSummary[]];
  tows: [PublicVesselSummary, ...PublicVesselSummary[]];
}

export type TrafficMovement = SingleVesselMovement | LikelyTowMovement;

/**
 * Resolve public relationship records against the tracks that are actually on
 * screen. A relationship without both a tug and a companion stays invisible,
 * so the UI never leaves an orphan bracket or connector behind.
 */
export function trafficMovements(
  vessels: PublicVesselSummary[],
  groups: PublicVesselGroup[],
): TrafficMovement[] {
  const vesselById = new Map(vessels.map((vessel) => [vessel.id, vessel]));
  const vesselOrder = new Map(vessels.map((vessel, index) => [vessel.id, index]));
  const resolvedGroups = groups
    .filter((group) => group.kind === "likely_tow")
    .map((group) => {
      const tugIdSet = new Set(group.tugIds);
      const tugs = group.tugIds
        .map((id) => vesselById.get(id))
        .filter((vessel): vessel is PublicVesselSummary => vessel !== undefined);
      const tows = group.towIds
        .filter((id) => !tugIdSet.has(id))
        .map((id) => vesselById.get(id))
        .filter((vessel): vessel is PublicVesselSummary => vessel !== undefined);
      const members = [...tugs, ...tows].sort(
        (a, b) => (vesselOrder.get(a.id) ?? 0) - (vesselOrder.get(b.id) ?? 0),
      );
      return { group, tugs, tows, members };
    })
    .filter(({ tugs, tows }) => tugs.length > 0 && tows.length > 0)
    .sort(
      (a, b) =>
        Math.min(...a.members.map((vessel) => vesselOrder.get(vessel.id) ?? 0))
        - Math.min(...b.members.map((vessel) => vesselOrder.get(vessel.id) ?? 0)),
    );

  const groupByVesselId = new Map<string, (typeof resolvedGroups)[number]>();
  for (const resolved of resolvedGroups) {
    for (const vessel of resolved.members) {
      if (!groupByVesselId.has(vessel.id)) groupByVesselId.set(vessel.id, resolved);
    }
  }

  const emittedGroupIds = new Set<string>();
  const movements: TrafficMovement[] = [];
  for (const vessel of vessels) {
    const resolved = groupByVesselId.get(vessel.id);
    if (!resolved) {
      movements.push({ kind: "single", id: `vessel:${vessel.id}`, vessels: [vessel] });
      continue;
    }
    if (emittedGroupIds.has(resolved.group.id)) continue;
    emittedGroupIds.add(resolved.group.id);
    movements.push({
      kind: "likely_tow",
      id: `tow:${resolved.group.id}`,
      group: resolved.group,
      vessels: resolved.members,
      tugs: resolved.tugs as [PublicVesselSummary, ...PublicVesselSummary[]],
      tows: resolved.tows as [PublicVesselSummary, ...PublicVesselSummary[]],
    });
  }
  return movements;
}

export function movementOrdinals(movements: TrafficMovement[]): Map<string, number> {
  return new Map(
    movements.flatMap((movement, index) =>
      movement.vessels.map((vessel) => [vessel.id, index + 1] as const),
    ),
  );
}

/**
 * Bring asynchronous AIS fixes in a likely tow to the freshest member time for
 * map placement only. The public observations remain untouched, and no track is
 * projected beyond the 90-second visual horizon without measured formation offsets.
 */
export function timeAlignedTowMembers(
  movement: LikelyTowMovement,
): Map<string, PublicVesselSummary> {
  const freshestObservedAtMs = Math.max(
    ...movement.vessels
      .map((vessel) => Date.parse(vessel.observedAt))
      .filter(Number.isFinite),
  );
  if (!Number.isFinite(freshestObservedAtMs)) {
    return new Map(movement.vessels.map((vessel) => [vessel.id, vessel]));
  }

  const newest = movement.vessels.reduce((a, b) => a.observedAt >= b.observedAt ? a : b);
  const offsets = movement.group.memberOffsetsMeters;
  return new Map(movement.vessels.map((vessel) => {
    const inferred = offsets?.[vessel.id] !== undefined && offsets[newest.id] !== undefined
      ? newest.sMeters + offsets[vessel.id]! - offsets[newest.id]!
      : projectVesselFix(vessel, freshestObservedAtMs).sMeters;
    const { minimum, maximum } = displayRouteBounds(vessel);
    const sMeters = Math.max(minimum, Math.min(maximum, inferred));
    return [vessel.id, { ...vessel, sMeters,
      corridorProgress: projectedCorridorProgress(vessel, sMeters) }];
  }));
}

export function likelyTowRelationship(
  vessel: PublicVesselSummary,
  movement: LikelyTowMovement,
): string {
  const tugIndex = movement.tugs.findIndex((candidate) => candidate.id === vessel.id);
  if (tugIndex >= 0) {
    return `towing ${joinNames(movement.tows)}`;
  }
  return `under tow by ${joinNames(movement.tugs)}`;
}

function joinNames(vessels: PublicVesselSummary[]): string {
  const names = vessels.map(vesselDisplayName);
  if (names.length <= 1) return names[0] ?? "another vessel";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}
